import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import WrapperPieComponent from '@/components/shared/charts/PieComponent/wrapper-pie-component';

const PlayersTierPoints = ({ player }) => {
    const theme = useTheme();

    // Obtener todas las puntuaciones
    const puntos = player?.estadisticas?.playerStats?.map((item) => Number(item?.tp ?? 0)) || [];
    const totalWeeks = player?.weeks?.length;

    const puntosDondeJugo =
        player?.estadisticas?.playerStats
            ?.filter((x) => x.s?.mp?.[0] > 0)
            ?.map((item) => Number(item?.tp ?? 0)) || [];

    const tiers = [
        { name: 'Tier 1', range: '10+ puntos', check: (p) => p >= 10, color: 'rgba(0, 82, 255, 0.8)' }, // Azul oscuro
        {
            name: 'Tier 2',
            range: '8-9 puntos',
            check: (p) => p >= 8 && p <= 9,
            color: 'rgba(0, 100, 0, 0.8)', // Verde oscuro
        },
        {
            name: 'Tier 3',
            range: '5-7 puntos',
            check: (p) => p >= 5 && p <= 7,
            color: 'rgba(34, 139, 34, 0.8)', // Verde bosque
        },
        {
            name: 'Tier 4',
            range: '3-4 puntos',
            check: (p) => p >= 3 && p <= 4,
            color: 'rgba(255, 140, 0, 0.8)', // Naranja oscuro
        },
        {
            name: 'Tier 5',
            range: '1-2 puntos',
            check: (p) => p >= 1 && p <= 2,
            color: 'rgba(204, 0, 0, 0.8)', // Rojo oscuro
        },
        { name: 'Tier 6', range: '≤ 0 puntos', check: (p) => p <= 0, color: 'rgba(105, 105, 105, 0.8)' }, // Gris oscuro
    ];

    // Contar cuántas veces cada Tier ha aparecido
    const tierCounts = tiers.map((tier) => ({
        ...tier,
        count: puntos.filter(tier.check).length,
    }));

    const tierCountsDondeJugo = tiers.map((tier) => ({
        ...tier,
        count: puntosDondeJugo.filter(tier.check).length,
    }));

    tierCounts.push({
        name: 'No jugó',
        range: '-',
        color: 'rgba(0, 0, 0, 0.7)', // Gris
        count: totalWeeks - puntos.length,
    });

    // Opciones para el gráfico de pastel
    const options = {
        responsive: true,
        plugins: {
            legend: { display: false },
        },
        rotation: -90, // Rotat
        cutout: '80%',
    };

    const PieChartCard = ({ title, labels, ranges, colors, data }) => {
        const accent3Color = theme.palette.border?.accent3 || theme.palette.accent3?.main || '#FF0080';

        return (
            <Box
                sx={{
                    position: 'relative',
                    p: 1.25,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: { xs: '100%', md: '100%' },
                    backgroundColor: theme.palette.background.paper,
                    border: 1,
                    borderColor: theme.palette.divider,
                    borderRadius: 2,
                    borderTop: '3px solid',
                    borderTopColor: accent3Color,
                }}
            >
                {title && <Typography sx={{ mb: 1, fontSize: '14px', fontWeight: 500 }}>{title}</Typography>}

                <Box display="flex" justifyContent="center" gap={'2%'} flexWrap="wrap">
                    {labels.map((label, index) => (
                        <Box
                            key={label}
                            sx={{
                                position: 'relative',
                                backgroundColor: theme.palette.background.darkCard,
                                border: 1,
                                borderColor: theme.palette.divider,
                                borderRadius: 1,
                                borderTop: '3px solid',
                                borderTopColor: colors[index],
                                p: 1,
                                fontSize: 12,
                                textAlign: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                aspectRatio: '1',
                            }}
                            mb={1}
                            width={{
                                xs: '30%',
                                md: 100,
                            }}
                        >
                            <Typography color={theme.palette.text.primary} fontSize={14}>
                                {label}
                            </Typography>
                            <Typography color={theme.palette.text.primary} fontSize={20} mt={0.5}>
                                {data[index]}
                            </Typography>
                            <Typography color={theme.palette.text.secondary} fontSize={14}>
                                {ranges[index]}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Box width={300} height={300} mt={2}>
                    <WrapperPieComponent
                        data={{ labels, datasets: [{ data, backgroundColor: colors }] }}
                        options={options}
                    />
                </Box>
            </Box>
        );
    };

    return (
        <Box
            width={{
                xs: '100%',
            }}
            gap={2}
            display={'flex'}
            flexDirection={{
                xs: 'column',
                md: 'row',
            }}
            mb={2}
        >
            <Box
                width={{
                    xs: '100%',
                    md: '50%',
                }}
            >
                <Typography
                    variant="h6"
                    sx={{ fontWeight: 500, mt: 2 }}
                    textAlign={'center'}
                    fontSize={16}
                    fontWeight={500}
                >
                    Distribución de Puntuaciones por Tier
                </Typography>
                <PieChartCard
                    title=""
                    labels={tierCounts.map((tier) => tier.name)}
                    ranges={tierCounts.map((tier) => tier.range)}
                    colors={tierCounts.map((tier) => tier.color)}
                    data={tierCounts.map((tier) => tier.count)}
                />
            </Box>

            <Box
                width={{
                    xs: '100%',
                    md: '50%',
                }}
            >
                <Typography variant="h6" sx={{ mt: 2 }} textAlign={'center'} fontSize={16} fontWeight={500}>
                    Distribución de Puntuaciones por Tier cuando jugó
                </Typography>
                <PieChartCard
                    title=""
                    labels={tierCountsDondeJugo.map((tier) => tier.name)}
                    ranges={tierCountsDondeJugo.map((tier) => tier.range)}
                    colors={tierCountsDondeJugo.map((tier) => tier.color)}
                    data={tierCountsDondeJugo.map((tier) => tier.count)}
                />
            </Box>
        </Box>
    );
};

export default PlayersTierPoints;
